import { Trash2, FolderOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatINR } from "@/lib/invoiceUtils";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SavedInvoicesList({
  open,
  onOpenChange,
  invoices,
  onLoad,
  onDelete,
}) {
  // Most recent first
  const sorted = [...(invoices || [])].sort((a, b) =>
    (b.updatedAt || "").localeCompare(a.updatedAt || ""),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="invoices-list-dialog"
        className="sm:max-w-[760px] rounded-none border-2 border-[#0A0A0A] p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 py-5 border-b-2 border-[#0A0A0A] bg-[#FAFAFA]">
          <DialogTitle className="text-xl font-extrabold tracking-tighter">
            Saved Invoices
            <span
              data-testid="invoices-list-count"
              className="ml-2 font-mono text-xs text-[#52525B]"
            >
              [{sorted.length}]
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#52525B]">
            Stored locally on this device. Click an invoice to load it for
            editing or re-printing. Removing it here does not affect any PDF or
            print you've already produced.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto scroll-clean">
          {sorted.length === 0 ? (
            <div
              data-testid="invoices-list-empty"
              className="px-6 py-12 text-center text-sm text-[#52525B]"
            >
              No invoices saved yet. Generate an invoice and click{" "}
              <span className="font-bold">Save</span> to store it locally.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b-2 border-[#0A0A0A]">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#52525B]">
                    Invoice #
                  </th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#52525B]">
                    Customer
                  </th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#52525B]">
                    Date
                  </th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#52525B]">
                    Total
                  </th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#52525B]">
                    Saved
                  </th>
                  <th className="px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((inv, idx) => (
                  <tr
                    key={inv.id}
                    data-testid={`invoice-row-${idx}`}
                    className="border-b border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors"
                  >
                    <td className="px-4 py-3 font-mono font-bold">
                      {inv.meta?.number || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {inv.customer?.name || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {fmtDate(inv.meta?.date)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold">
                      ₹ {formatINR(inv.totals?.total || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[10px] text-[#52525B]">
                      {fmtDate(inv.updatedAt)}{" "}
                      <span className="opacity-60">
                        {fmtTime(inv.updatedAt)}
                      </span>
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => {
                          onLoad?.(inv);
                          onOpenChange(false);
                        }}
                        data-testid={`invoice-load-${idx}`}
                        title="Load for editing / reprint"
                        className="inline-flex items-center gap-1 border border-[#0A0A0A] px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white transition-colors mr-1"
                      >
                        <FolderOpen size={11} strokeWidth={2.5} />
                        Load
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete?.(inv.id)}
                        data-testid={`invoice-delete-${idx}`}
                        title="Delete this saved invoice"
                        aria-label="Delete invoice"
                        className="inline-flex items-center justify-center w-8 h-7 border border-[#E5E5E5] hover:border-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-colors"
                      >
                        <Trash2 size={11} strokeWidth={2.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
